import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const HistorialDevolucionesAvanzado = () => {
    const navigate = useNavigate();
    const { token, user } = useAuth();

    // Estados principales
    const [devoluciones, setDevoluciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Estados para filtros avanzados
    const [filtros, setFiltros] = useState({
        fechaInicio: '',
        fechaFin: '',
        cliente: '',
        vendedor: '',
        producto: '',
        formaDevolucion: '',
        idVenta: '',
        montoMinimo: '',
        montoMaximo: ''
    });

    // Estados para visualización y estadísticas
    const [vistaActual, setVistaActual] = useState('lista'); // 'lista', 'agrupada', 'estadisticas'
    const [estadisticas, setEstadisticas] = useState({
        totalDevoluciones: 0,
        montoTotalDevuelto: 0,
        devolucionesPorForma: {},
        devolucionesPorDia: {},
        productosDevueltos: {},
        clientesConDevoluciones: {}
    });

    // Estados para paginación y ordenamiento
    const [paginaActual, setPaginaActual] = useState(1);
    const [devolucionesPorPagina] = useState(20);
    const [ordenarPor, setOrdenarPor] = useState('fecha_desc');
    const [devolucionesAgrupadas, setDevolucionesAgrupadas] = useState({});

    // Cargar devoluciones con filtros
    const cargarDevoluciones = async () => {
        setLoading(true);
        setError('');

        try {
            const params = new URLSearchParams();

            // Agregar filtros que tengan valor
            Object.entries(filtros).forEach(([key, value]) => {
                if (value && value.trim() !== '') {
                    params.append(key, value);
                }
            });

            // Agregar parámetros de paginación y ordenamiento
            params.append('pagina', paginaActual);
            params.append('limite', devolucionesPorPagina);
            params.append('ordenar', ordenarPor);

            const response = await axios.get(`${import.meta.env.VITE_API_URL}/devoluciones?${params}`, {
                headers: { 'x-auth-token': token }
            });

            const devolucionesData = response.data.devoluciones || response.data || [];
            setDevoluciones(devolucionesData);
            calcularEstadisticas(devolucionesData);

            // Agrupar devoluciones por venta
            const agrupadas = agruparDevolucionesPorVenta(devolucionesData);
            setDevolucionesAgrupadas(agrupadas);

        } catch (err) {
            console.error('Error al cargar devoluciones:', err);
            setError('Error al cargar el historial de devoluciones');
        } finally {
            setLoading(false);
        }
    };

    // Calcular estadísticas
    const calcularEstadisticas = (devolucionesData) => {
        const totalDevoluciones = devolucionesData.length;
        const montoTotalDevuelto = devolucionesData.reduce((total, dev) => total + parseFloat(dev.monto_devuelto || 0), 0);

        const devolucionesPorForma = devolucionesData.reduce((acc, dev) => {
            const forma = dev.forma_devolucion || 'sin_especificar';
            acc[forma] = (acc[forma] || 0) + parseFloat(dev.monto_devuelto || 0);
            return acc;
        }, {});

        const devolucionesPorDia = devolucionesData.reduce((acc, dev) => {
            const fecha = new Date(dev.createdAt).toISOString().split('T')[0];
            acc[fecha] = (acc[fecha] || 0) + 1;
            return acc;
        }, {});

        const productosDevueltos = devolucionesData.reduce((acc, dev) => {
            const producto = dev.producto?.nombre_producto || 'Producto eliminado';
            acc[producto] = (acc[producto] || 0) + parseFloat(dev.cantidad_devuelta || 0);
            return acc;
        }, {});

        const clientesConDevoluciones = devolucionesData.reduce((acc, dev) => {
            const cliente = dev.venta?.cliente?.nombre || 'Consumidor final';
            acc[cliente] = (acc[cliente] || 0) + parseFloat(dev.monto_devuelto || 0);
            return acc;
        }, {});

        setEstadisticas({
            totalDevoluciones,
            montoTotalDevuelto,
            devolucionesPorForma,
            devolucionesPorDia,
            productosDevueltos,
            clientesConDevoluciones
        });
    };

    // Agrupar devoluciones por venta para mejor visualización
    const agruparDevolucionesPorVenta = (devoluciones) => {
        const agrupadas = {};

        devoluciones.forEach(devolucion => {
            const idVenta = devolucion.id_venta;
            if (!agrupadas[idVenta]) {
                agrupadas[idVenta] = {
                    venta: devolucion.venta,
                    devoluciones: [],
                    montoTotalDevuelto: 0,
                    fechaPrimera: devolucion.createdAt,
                    fechaUltima: devolucion.createdAt
                };
            }

            agrupadas[idVenta].devoluciones.push(devolucion);
            agrupadas[idVenta].montoTotalDevuelto += parseFloat(devolucion.monto_devuelto || 0);

            // Actualizar fechas extremas
            if (new Date(devolucion.createdAt) < new Date(agrupadas[idVenta].fechaPrimera)) {
                agrupadas[idVenta].fechaPrimera = devolucion.createdAt;
            }
            if (new Date(devolucion.createdAt) > new Date(agrupadas[idVenta].fechaUltima)) {
                agrupadas[idVenta].fechaUltima = devolucion.createdAt;
            }
        });

        return agrupadas;
    };

    // Manejar cambios en filtros
    const handleFiltroChange = (campo, valor) => {
        setFiltros(prev => ({
            ...prev,
            [campo]: valor
        }));
        setPaginaActual(1); // Resetear a primera página
    };

    // Limpiar filtros
    const limpiarFiltros = () => {
        setFiltros({
            fechaInicio: '',
            fechaFin: '',
            cliente: '',
            vendedor: '',
            producto: '',
            formaDevolucion: '',
            idVenta: '',
            montoMinimo: '',
            montoMaximo: ''
        });
        setPaginaActual(1);
    };

    // Exportar a CSV
    const exportarCSV = () => {
        const headers = [
            'ID Devolución',
            'ID Venta',
            'Fecha',
            'Cliente',
            'Vendedor',
            'Producto',
            'Cantidad Devuelta',
            'Monto Devuelto',
            'Forma Devolución',
            'Motivo'
        ];

        const csvContent = [
            headers.join(','),
            ...devoluciones.map(dev => [
                dev.id,
                dev.id_venta,
                new Date(dev.createdAt).toLocaleDateString(),
                dev.venta?.cliente?.nombre || 'Consumidor final',
                dev.venta?.usuario?.nombre_user || 'Sin vendedor',
                dev.producto?.nombre_producto || 'Producto eliminado',
                dev.cantidad_devuelta,
                dev.monto_devuelto,
                dev.forma_devolucion,
                `"${dev.motivo}"`
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `historial_devoluciones_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Formatear moneda
    const formatearMoneda = (monto) => {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS'
        }).format(monto);
    };

    // Formatear fecha
    const formatearFecha = (fecha) => {
        return new Date(fecha).toLocaleString('es-AR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Obtener color según forma de devolución
    const getColorFormaDevolucion = (forma) => {
        const colores = {
            'efectivo': 'bg-green-100 text-green-800',
            'cuenta_corriente': 'bg-blue-100 text-blue-800',
            'nota_credito': 'bg-purple-100 text-purple-800'
        };
        return colores[forma] || 'bg-gray-100 text-gray-800';
    };

    // Efectos
    useEffect(() => {
        cargarDevoluciones();
    }, [filtros, paginaActual, ordenarPor]);




    return (
        <div className="container mx-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 p-12">
                <div className="flex items-center">

                    <h1 className="text-3xl font-bold">Historial de Devoluciones Avanzado</h1>
                </div>

                <div className="flex gap-2">
                    {/* Selector de vista */}
                    <div className="flex bg-gray-100 rounded">
                        <button
                            onClick={() => setVistaActual('lista')}
                            className={`px-3 py-1 rounded ${vistaActual === 'lista' ? 'bg-blue-500 text-white' : 'text-gray-700'}`}
                        >
                            Lista
                        </button>
                        <button
                            onClick={() => setVistaActual('agrupada')}
                            className={`px-3 py-1 rounded ${vistaActual === 'agrupada' ? 'bg-blue-500 text-white' : 'text-gray-700'}`}
                        >
                            Por Venta
                        </button>
                        <button
                            onClick={() => setVistaActual('estadisticas')}
                            className={`px-3 py-1 rounded ${vistaActual === 'estadisticas' ? 'bg-blue-500 text-white' : 'text-gray-700'}`}
                        >
                            Estadísticas
                        </button>
                    </div>

                    <button
                        onClick={exportarCSV}
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        disabled={devoluciones.length === 0}
                    >
                        📊 Exportar CSV
                    </button>
                </div>
            </div>

            {/* Tarjetas de estadísticas rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-500">Total Devoluciones</h3>
                    <p className="text-2xl font-bold text-blue-600">{estadisticas.totalDevoluciones}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-500">Monto Total Devuelto</h3>
                    <p className="text-2xl font-bold text-red-600">{formatearMoneda(estadisticas.montoTotalDevuelto)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-500">Promedio por Devolución</h3>
                    <p className="text-2xl font-bold text-purple-600">
                        {estadisticas.totalDevoluciones > 0
                            ? formatearMoneda(estadisticas.montoTotalDevuelto / estadisticas.totalDevoluciones)
                            : formatearMoneda(0)
                        }
                    </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-500">Ventas con Devoluciones</h3>
                    <p className="text-2xl font-bold text-orange-600">{Object.keys(devolucionesAgrupadas).length}</p>
                </div>
            </div>

            {/* Panel de filtros avanzados */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Filtros Avanzados</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={limpiarFiltros}
                            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                        >
                            🗑️ Limpiar
                        </button>
                        <button
                            onClick={cargarDevoluciones}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                        >
                            🔍 Buscar
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
                        <input
                            type="date"
                            value={filtros.fechaInicio}
                            onChange={(e) => handleFiltroChange('fechaInicio', e.target.value)}
                            className="w-full px-3 py-2 border rounded text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
                        <input
                            type="date"
                            value={filtros.fechaFin}
                            onChange={(e) => handleFiltroChange('fechaFin', e.target.value)}
                            className="w-full px-3 py-2 border rounded text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ID Venta</label>
                        <input
                            type="number"
                            value={filtros.idVenta}
                            onChange={(e) => handleFiltroChange('idVenta', e.target.value)}
                            placeholder="Ej: 123"
                            className="w-full px-3 py-2 border rounded text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Forma Devolución</label>
                        <select
                            value={filtros.formaDevolucion}
                            onChange={(e) => handleFiltroChange('formaDevolucion', e.target.value)}
                            className="w-full px-3 py-2 border rounded text-sm"
                        >
                            <option value="">Todas</option>
                            <option value="efectivo">Efectivo</option>
                            <option value="cuenta_corriente">Cuenta Corriente</option>
                            <option value="nota_credito">Nota de Crédito</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                        <input
                            type="text"
                            value={filtros.cliente}
                            onChange={(e) => handleFiltroChange('cliente', e.target.value)}
                            placeholder="Nombre del cliente"
                            className="w-full px-3 py-2 border rounded text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Producto</label>
                        <input
                            type="text"
                            value={filtros.producto}
                            onChange={(e) => handleFiltroChange('producto', e.target.value)}
                            placeholder="Nombre del producto"
                            className="w-full px-3 py-2 border rounded text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Monto Mínimo</label>
                        <input
                            type="number"
                            value={filtros.montoMinimo}
                            onChange={(e) => handleFiltroChange('montoMinimo', e.target.value)}
                            placeholder="0.00"
                            step="0.01"
                            className="w-full px-3 py-2 border rounded text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Monto Máximo</label>
                        <input
                            type="number"
                            value={filtros.montoMaximo}
                            onChange={(e) => handleFiltroChange('montoMaximo', e.target.value)}
                            placeholder="999999.99"
                            step="0.01"
                            className="w-full px-3 py-2 border rounded text-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Mostrar error */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        className="bg-red-500 text-white p-4 rounded mb-4"
                    >
                        <div className="flex justify-between items-center">
                            <p>{error}</p>
                            <button onClick={() => setError('')} className="text-xl">×</button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Contenido principal */}
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow p-6">
                    {vistaActual === 'lista' && (
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold">Lista de Devoluciones ({devoluciones.length})</h2>
                                <select
                                    value={ordenarPor}
                                    onChange={(e) => setOrdenarPor(e.target.value)}
                                    className="px-3 py-1 border rounded text-sm"
                                >
                                    <option value="fecha_desc">Fecha (más reciente)</option>
                                    <option value="fecha_asc">Fecha (más antigua)</option>
                                    <option value="monto_desc">Monto (mayor)</option>
                                    <option value="monto_asc">Monto (menor)</option>
                                </select>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="border-b bg-gray-50">
                                            <th className="text-left p-3 text-sm font-medium">ID</th>
                                            <th className="text-left p-3 text-sm font-medium">Fecha</th>
                                            <th className="text-left p-3 text-sm font-medium">Venta</th>
                                            <th className="text-left p-3 text-sm font-medium">Cliente</th>
                                            <th className="text-left p-3 text-sm font-medium">Producto</th>
                                            <th className="text-left p-3 text-sm font-medium">Cantidad</th>
                                            <th className="text-left p-3 text-sm font-medium">Monto</th>
                                            <th className="text-left p-3 text-sm font-medium">Forma</th>
                                            <th className="text-left p-3 text-sm font-medium">Motivo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {devoluciones.map(devolucion => (
                                            <tr key={devolucion.id} className="border-b hover:bg-gray-50">
                                                <td className="p-3 text-sm font-mono">#{devolucion.id}</td>
                                                <td className="p-3 text-sm">{formatearFecha(devolucion.createdAt)}</td>
                                                <td className="p-3 text-sm">
                                                    <span className="font-mono">#{devolucion.id_venta}</span>
                                                </td>
                                                <td className="p-3 text-sm">
                                                    {devolucion.venta?.cliente?.nombre || 'Consumidor final'}
                                                </td>
                                                <td className="p-3 text-sm">
                                                    <div className="max-w-32 truncate" title={devolucion.producto?.nombre_producto}>
                                                        {devolucion.producto?.nombre_producto || 'Producto eliminado'}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-sm font-mono">
                                                    {parseFloat(devolucion.cantidad_devuelta).toFixed(3)}
                                                </td>
                                                <td className="p-3 text-sm font-mono font-semibold text-red-600">
                                                    {formatearMoneda(devolucion.monto_devuelto)}
                                                </td>
                                                <td className="p-3 text-sm">
                                                    <span className={`px-2 py-1 rounded text-xs ${getColorFormaDevolucion(devolucion.forma_devolucion)}`}>
                                                        {devolucion.forma_devolucion?.replace('_', ' ').toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-sm">
                                                    <div className="max-w-32 truncate" title={devolucion.motivo}>
                                                        {devolucion.motivo}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {devoluciones.length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                    No se encontraron devoluciones con los filtros aplicados
                                </div>
                            )}
                        </div>
                    )}

                    {vistaActual === 'agrupada' && (
                        <div>
                            <h2 className="text-lg font-semibold mb-4">Devoluciones Agrupadas por Venta ({Object.keys(devolucionesAgrupadas).length})</h2>

                            <div className="space-y-4">
                                {Object.entries(devolucionesAgrupadas).map(([idVenta, grupo]) => (
                                    <motion.div
                                        key={idVenta}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="border rounded-lg p-4 bg-gray-50"
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <h3 className="font-semibold text-lg">Venta #{idVenta}</h3>
                                                <p className="text-sm text-gray-600">
                                                    Cliente: {grupo.venta?.cliente?.nombre || 'Consumidor final'}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    Total Original: {formatearMoneda(grupo.venta?.total || 0)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-lg font-bold text-red-600">
                                                    {formatearMoneda(grupo.montoTotalDevuelto)}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    {grupo.devoluciones.length} devolución(es)
                                                </p>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            {grupo.devoluciones.map(devolucion => (
                                                <div key={devolucion.id} className="bg-white p-3 rounded border text-sm">
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            <span className="font-medium">{devolucion.producto?.nombre_producto}</span>
                                                            <span className="ml-2 text-gray-600">
                                                                Cant: {parseFloat(devolucion.cantidad_devuelta).toFixed(3)}
                                                            </span>
                                                            <span className={`ml-2 px-2 py-1 rounded text-xs ${getColorFormaDevolucion(devolucion.forma_devolucion)}`}>
                                                                {devolucion.forma_devolucion?.replace('_', ' ').toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="font-semibold">{formatearMoneda(devolucion.monto_devuelto)}</div>
                                                            <div className="text-xs text-gray-500">{formatearFecha(devolucion.createdAt)}</div>
                                                        </div>
                                                    </div>
                                                    {devolucion.motivo && (
                                                        <div className="mt-1 text-xs text-gray-600 italic">
                                                            Motivo: {devolucion.motivo}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {Object.keys(devolucionesAgrupadas).length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                    No se encontraron devoluciones con los filtros aplicados
                                </div>
                            )}
                        </div>
                    )}

                    {vistaActual === 'estadisticas' && (
                        <div>
                            <h2 className="text-lg font-semibold mb-6">Estadísticas Detalladas</h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Devoluciones por forma */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold mb-3">Por Forma de Devolución</h3>
                                    <div className="space-y-2">
                                        {Object.entries(estadisticas.devolucionesPorForma).map(([forma, monto]) => (
                                            <div key={forma} className="flex justify-between items-center">
                                                <span className={`px-2 py-1 rounded text-xs ${getColorFormaDevolucion(forma)}`}>
                                                    {forma.replace('_', ' ').toUpperCase()}
                                                </span>
                                                <span className="font-semibold">{formatearMoneda(monto)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Top productos devueltos */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold mb-3">Productos Más Devueltos</h3>
                                    <div className="space-y-2">
                                        {Object.entries(estadisticas.productosDevueltos)
                                            .sort((a, b) => b[1] - a[1])
                                            .slice(0, 5)
                                            .map(([producto, cantidad]) => (
                                                <div key={producto} className="flex justify-between items-center text-sm">
                                                    <span className="truncate max-w-32" title={producto}>{producto}</span>
                                                    <span className="font-semibold">{parseFloat(cantidad).toFixed(3)}</span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>

                                {/* Top clientes con devoluciones */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold mb-3">Clientes con Más Devoluciones</h3>
                                    <div className="space-y-2">
                                        {Object.entries(estadisticas.clientesConDevoluciones)
                                            .sort((a, b) => b[1] - a[1])
                                            .slice(0, 5)
                                            .map(([cliente, monto]) => (
                                                <div key={cliente} className="flex justify-between items-center text-sm">
                                                    <span className="truncate max-w-32" title={cliente}>{cliente}</span>
                                                    <span className="font-semibold text-red-600">{formatearMoneda(monto)}</span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>

                                {/* Devoluciones por día */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold mb-3">Devoluciones por Día (Últimos 7)</h3>
                                    <div className="space-y-2">
                                        {Object.entries(estadisticas.devolucionesPorDia)
                                            .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                                            .slice(0, 7)
                                            .map(([fecha, cantidad]) => (
                                                <div key={fecha} className="flex justify-between items-center text-sm">
                                                    <span>{new Date(fecha).toLocaleDateString()}</span>
                                                    <span className="font-semibold">{cantidad}</span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default HistorialDevolucionesAvanzado;
